<template>
    <div>
        <!-- Section Header -->
        <div class="flex flex-col gap-4 pb-2 pt-1 md:flex-row md:items-start md:justify-between">
            <div class="flex flex-col gap-4">
                <!-- Auto-ESO Toggle -->
                <div>
                    <SwitchGroup as="div" class="flex items-center">
                        <Switch
                            :class="[autoEsoEnabled ? 'bg-gray-700' : 'bg-gray-200', userGlobalAutoEsoActive ? 'opacity-60' : '', isSharedViewOnly ? 'opacity-50 cursor-not-allowed' : '', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2']"
                            @click.prevent="handleAutoEsoToggleClick"
                        >
                            <span aria-hidden="true" :class="[autoEsoEnabled ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                        </Switch>
                        <SwitchLabel as="span" passive class="ml-3 text-sm flex items-center gap-1 flex-wrap">
                            <span class="font-medium text-gray-900">Automatically Apply Maximum Risk Reduction to All Applicants</span>
                            <ToolTip
                                v-if="userGlobalAutoEsoActive"
                                title="Account-wide auto screening"
                                :description="userGlobalAutoEsoTooltipDescription"
                            />
                            <ToolTip
                                v-if="isSharedViewOnly && viewOnlyTooltip"
                                :title="viewOnlyTooltip.title"
                                :description="viewOnlyTooltip.description"
                            />
                        </SwitchLabel>
                    </SwitchGroup>
                </div>
                <div v-show="applications.length > 0">
                    <SwitchGroup as="div" class="flex items-center">
                        <Switch
                            :class="[showArchived ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']"
                            @click="show_archived_applications()"
                        >
                            <span aria-hidden="true" :class="[showArchived ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                        </Switch>
                        <SwitchLabel as="span" passive class="ml-3 text-sm">
                            <span class="font-medium text-gray-900">Show Archived Applications</span>
                        </SwitchLabel>
                    </SwitchGroup>
                </div>
                <div v-show="archivableApplications.length > 0 && !isSharedViewOnly">
                    <button
                        type="button"
                        :disabled="selectedApplicationIds.length === 0 || isArchivingApplications"
                        data-test="archive-selected-button"
                        class="inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50"
                        @click="openArchiveApplicationsModal(selectedApplicationIds)"
                    >
                        <font-awesome-icon :icon="faBoxArchive" class="h-4 w-4" aria-hidden="true" />
                        Archive Selected
                        <span v-if="selectedApplicationIds.length" class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700">{{ selectedApplicationIds.length }}</span>
                    </button>
                </div>
            </div>
            <div class="flex shrink-0 items-center gap-2 md:pt-0.5">
                <button
                    type="button"
                    class="relative inline-flex items-center px-3 py-2 text-sm font-semibold text-white bg-rent-risk-blue-400 hover:bg-rent-risk-blue-500 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 disabled:pointer-events-none disabled:opacity-50"
                    :class="isSharedViewOnly ? 'bg-rent-risk-blue-300' : ''"
                    :disabled="isSharedViewOnly"
                    @click="openScreenApplicantsModal"
                >
                    Start Screening Tenants
                </button>
                <ToolTip
                    v-if="isSharedViewOnly && viewOnlyTooltip"
                    :title="viewOnlyTooltip.title"
                    :description="viewOnlyTooltip.description"
                />
            </div>
        </div>

        <HowItWorksBanner
            description="Follow these simple steps to find the right tenant with confidence."
            :steps="howItWorksSteps"
            storage-key="rentrisk-tenant-screening-how-it-works-dismissed"
        />
        
        <!-- Empty State -->
        <div v-show="applications.length === 0">
            <div class="rounded bg-blue-50 border border-t-blue-800 border-t-4 border-blue-400 py-4 px-2">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <font-awesome-icon :icon="faInfoCircle" class=" text-blue-700" aria-hidden="true" />
                    </div>
                    <div class="ml-3 flex-1 md:flex md:justify-between">
                        <p class="text-sm text-blue-700">
                            No Applications have been submitted for this property.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Applications List (Desktop) and Cards (Mobile) -->
        <div v-show="applications.length > 0">
            <!-- Desktop table -->
            <div class="hidden lg:block overflow-x-auto">
                <table class="w-full table-fixed border-separate border-spacing-y-3">
                    <colgroup>
                        <col class="w-3" />
                        <col v-if="!isSharedViewOnly" class="w-10" />
                        <col class="w-72" />
                        <col class="w-36" />
                        <col class="w-40" />
                        <col class="w-24" />
                        <col v-if="property.required_two_step" class="w-44" />
                        <col class="w-44" />
                    </colgroup>
                    <thead>
                        <tr class="text-sm font-semibold text-gray-700">
                            <th scope="col" class="rounded-l-lg bg-gray-50 py-3 pl-3 pr-0">
                                <span class="sr-only">Status</span>
                            </th>
                            <th v-if="!isSharedViewOnly" scope="col" class="bg-gray-50 px-3 py-3 text-center">
                                <input
                                    type="checkbox"
                                    :checked="allArchivableSelected"
                                    :disabled="archivableApplications.length === 0"
                                    aria-label="Select all open applications"
                                    data-test="select-all-applications"
                                    class="h-4 w-4 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                    @change="toggleSelectAllApplications"
                                />
                            </th>
                            <th scope="col" class="bg-gray-50 px-4 py-3 text-left">Applicant</th>
                            <th scope="col" class="bg-gray-50 px-4 py-3 text-center">Started On</th>
                            <th scope="col" class="bg-gray-50 px-4 py-3 text-center">Progress</th>
                            <th scope="col" class="bg-gray-50 px-4 py-3 text-center">
                                <span class="inline-flex items-center justify-center">
                                    <span>Risk</span>
                                    <ToolTip
                                        title="Risk"
                                        :description="riskMeterTooltipDescription"
                                    />
                                </span>
                            </th>
                            <th v-if="property.required_two_step" scope="col" class="bg-gray-50 px-4 py-3 text-center text-xs leading-snug">
                                Criminal/Background Report
                            </th>
                            <th scope="col" class="rounded-r-lg bg-gray-50 px-4 py-3 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template
                            v-for="(application, index) in applications"
                            :key="`desktop-${index}`"
                        >
                            <tr
                                v-if="showArchived && application.agent_application_display === 0 || application.agent_application_display === 1"
                                class="text-sm text-gray-600"
                                :class="application.agent_application_display === 0 ? 'bg-red-50/60' : 'bg-white'"
                            >
                                <td class="rounded-l-lg border border-r-0 border-gray-200 py-4 pl-3 pr-0 align-middle shadow-sm">
                                    <div
                                        class="group relative flex min-h-[4.5rem] items-stretch"
                                        :title="application.meets_screening_rules === false ? screeningRulesFlagTooltip : undefined"
                                    >
                                        <span
                                            class="block w-1 shrink-0 rounded-full"
                                            :class="applicationStatusBarClass(application)"
                                            aria-hidden="true"
                                        />
                                        <span
                                            v-if="application.meets_screening_rules === false"
                                            class="pointer-events-none absolute left-full top-1/2 z-30 ml-2 hidden w-max max-w-xs -translate-y-1/2 rounded-md bg-gray-900 px-2.5 py-1.5 text-left text-xs font-medium text-white shadow-lg group-hover:block"
                                            role="tooltip"
                                        >
                                            {{ screeningRulesFlagTooltip }}
                                        </span>
                                    </div>
                                </td>
                                <td v-if="!isSharedViewOnly" class="border-y border-gray-200 px-3 py-4 text-center align-middle shadow-sm">
                                    <input
                                        v-if="application.agent_application_display === 1"
                                        v-model="selectedApplicationIds"
                                        type="checkbox"
                                        :value="application.id"
                                        :aria-label="`Select application from ${application.user?.name || 'applicant'}`"
                                        data-test="select-application"
                                        class="h-4 w-4 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                    />
                                </td>
                                <td class="border-y border-gray-200 px-4 py-4 align-middle shadow-sm">
                                    <div class="flex items-center gap-3">
                                        <div
                                            class="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-gray-200"
                                            :class="hasApplicantProfilePhoto(application) ? 'bg-white' : 'bg-slate-100 text-sm font-semibold text-slate-600'"
                                            @click="clickList(application)"
                                        >
                                            <img
                                                v-if="hasApplicantProfilePhoto(application)"
                                                :src="application.user.profile_photo_url"
                                                :alt="application.user?.name || 'Applicant'"
                                                class="h-full w-full object-cover"
                                            />
                                            <span v-else>{{ applicantInitials(application.user?.name) }}</span>
                                        </div>
                                        <div class="min-w-0">
                                            <p class="truncate font-semibold text-gray-900" @click="clickList(application)">{{ application.user?.name }}</p>
                                            <p class="truncate text-sm text-gray-500" @click="clickList(application)">{{ application.user?.email }}</p>
                                            <p
                                                v-if="hasCoApplicantInvited(application)"
                                                class="text-xs font-medium text-blue-600"
                                            >
                                                Co-Applicant Invited
                                            </p>
                                            <p v-show="showErrorMessage[application.id]" class="mt-1 text-xs text-red-700">
                                                Please ask the applicant to submit personal information.
                                            </p>
                                        </div>
                                    </div>
                                </td>
                                <td class="border-y border-gray-200 px-4 py-4 text-center align-middle shadow-sm" @click="clickList(application)">
                                    <span class="inline-flex items-center justify-center gap-2">
                                        <font-awesome-icon :icon="faCalendar" class="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                        <time :datetime="application.created_at">{{ application.started_on_display || application.created_at }}</time>
                                    </span>
                                </td>
                                <td class="border-y border-gray-200 px-4 py-4 text-center align-middle shadow-sm" @click="clickList(application)">
                                    <div v-if="application.is_complete" class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                        Complete
                                    </div>
                                    <div v-else class="mx-auto flex w-full max-w-[9.5rem] items-center gap-3">
                                        <div class="relative group min-w-0 flex-1">
                                            <div class="h-2.5 rounded-full bg-gray-200">
                                                <div
                                                    class="h-2.5 rounded-full bg-blue-600 transition-all duration-300 ease-in-out"
                                                    :style="{ width: (application.completion_percentage ?? 0) + '%' }"
                                                />
                                            </div>
                                            <div class="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 group-hover:block">
                                                <div class="whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">
                                                    {{ application.steps_completed ?? 0 }}/{{ application.total_steps ?? 0 }} ({{ application.current_step_display_name || 'Getting Started' }})
                                                </div>
                                            </div>
                                        </div>
                                        <span class="shrink-0 text-sm font-medium tabular-nums text-gray-700">
                                            {{ application.completion_percentage ?? 0 }}%
                                        </span>
                                    </div>
                                </td>
                                <td class="border-y border-gray-200 px-4 py-4 text-center align-middle shadow-sm" @click="clickList(application)">
                                    <div class="flex justify-center">
                                        <RiskMeter :addon-count="(application.required_addons?.length || 0) + (hasProtectAccess ? 1 : 0)" :width="72" />
                                    </div>
                                </td>
                                <td v-if="property.required_two_step" class="border-y border-gray-200 px-4 py-4 text-center align-middle shadow-sm">
                                    <template v-if="application.agent_requests_criminal_background && !application.tenant_approves_criminal_background">
                                        <span @click="clickList(application)">Request Sent</span>
                                    </template>
                                    <template v-else-if="application.agent_requests_criminal_background && application.tenant_approves_criminal_background">
                                        <span v-if="!property.deleted_at" class="inline-flex items-center justify-center" @click="clickList(application)">
                                            <span v-if="application.share_id" class="text-green-600">
                                                <font-awesome-icon :icon="faCircleCheck" class="h-6 w-6" />
                                            </span>
                                            <span v-else class="text-red-700">
                                                <font-awesome-icon :icon="faCircleXmark" class="h-6 w-6" />
                                            </span>
                                        </span>
                                    </template>
                                    <template v-else>
                                        <button
                                            v-if="application.report"
                                            type="button"
                                            :disabled="isSharedViewOnly"
                                            class="inline-flex items-center justify-center rounded-md bg-gray-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-gray-700 disabled:pointer-events-none disabled:opacity-50"
                                            @click.prevent="openRequestModalAlert(application)"
                                        >
                                            Click to Request
                                        </button>
                                        <span v-else @click="clickList(application)">N/A</span>
                                    </template>
                                </td>
                                <td class="rounded-r-lg border border-l-0 border-gray-200 px-3 py-4 text-center align-middle shadow-sm">
                                    <div class="mx-auto flex w-full max-w-[9.5rem] flex-col items-stretch justify-center gap-2.5">
                                        <button
                                            v-if="application.required_addons?.length < addons.length"
                                            type="button"
                                            :disabled="isSharedViewOnly"
                                            class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-rent-risk-blue-400 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 disabled:pointer-events-none disabled:opacity-50"
                                            @click.stop="openEnhancedScreeningOptionsModal(application)"
                                        >
                                            <font-awesome-icon :icon="faShieldCheck" class="h-3.5 w-3.5" aria-hidden="true" />
                                            Lower My Risk
                                        </button>
                                        <button
                                            v-if="application.required_addons?.length"
                                            type="button"
                                            :disabled="isSharedViewOnly"
                                            class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-rent-risk-blue-400 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 disabled:pointer-events-none disabled:opacity-50"
                                            @click.stop="showApplicationEnhancedScreeningOptions(application)"
                                        >
                                            Verified Reports
                                            <font-awesome-icon :icon="faChevronRight" class="h-3 w-3" aria-hidden="true" />
                                        </button>
                                        <button
                                            v-if="application.personal_information && application.agent_application_display === 1"
                                            type="button"
                                            class="inline-flex items-center text-xs font-semibold text-gray-700 transition-colors hover:text-rent-risk-blue-400"
                                            @click="clickList(application)"
                                        >
                                            View Application
                                            <font-awesome-icon :icon="faChevronRight" class="ml-1 h-3 w-3" aria-hidden="true" />
                                        </button>
                                        <button
                                            v-if="application.agent_application_display === 1"
                                            type="button"
                                            :disabled="isSharedViewOnly || isArchivingApplications"
                                            data-test="archive-application"
                                            class="inline-flex items-center justify-center gap-1 text-xs font-semibold text-gray-500 transition-colors hover:text-red-700 disabled:pointer-events-none disabled:opacity-50"
                                            @click.stop="openArchiveApplicationsModal([application.id])"
                                        >
                                            <font-awesome-icon :icon="faBoxArchive" class="h-3 w-3" aria-hidden="true" />
                                            Archive
                                        </button>
                                        <button
                                            v-if="application.agent_application_display === 0"
                                            type="button"
                                            :disabled="isSharedViewOnly"
                                            class="rounded-md bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20 hover:bg-green-100 disabled:pointer-events-none disabled:opacity-50"
                                            @click="restoreApplication(application)"
                                        >
                                            Restore Application
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <!-- Mobile Cards View -->
            <div class="lg:hidden">
                <ul role="list" class="my-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <li v-for="(application, index) in applications" :key="index">
                        <template v-if="showArchived && application.agent_application_display === 0 || application.agent_application_display === 1">
                            <div
                                class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
                                :class="application.agent_application_display === 0 ? 'bg-red-50/60' : ''"
                                :title="application.meets_screening_rules === false ? screeningRulesFlagTooltip : undefined"
                            >
                                <div class="flex">
                                    <span
                                        class="w-1 shrink-0"
                                        :class="applicationStatusBarClass(application)"
                                        aria-hidden="true"
                                    />
                                    <div class="min-w-0 flex-1 divide-y divide-gray-100">
                                        <div class="flex items-center gap-3 p-4">
                                            <div
                                                class="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-gray-200"
                                                :class="hasApplicantProfilePhoto(application) ? 'bg-white' : 'bg-slate-100 text-sm font-semibold text-slate-600'"
                                            >
                                                <img
                                                    v-if="hasApplicantProfilePhoto(application)"
                                                    :src="application.user.profile_photo_url"
                                                    :alt="application.user?.name || 'Applicant'"
                                                    class="h-full w-full object-cover"
                                                />
                                                <span v-else>{{ applicantInitials(application.user?.name) }}</span>
                                            </div>
                                            <div class="min-w-0 flex-1">
                                                <p class="text-sm font-semibold text-gray-900">{{ application.user?.name }}</p>
                                                <p class="truncate text-sm text-gray-500">{{ application.user?.email }}</p>
                                                <p
                                                    v-if="hasCoApplicantInvited(application)"
                                                    class="text-xs font-medium text-blue-600"
                                                >
                                                    Co-Applicant Invited
                                                </p>
                                            </div>
                                            <input
                                                v-if="!isSharedViewOnly && application.agent_application_display === 1"
                                                v-model="selectedApplicationIds"
                                                type="checkbox"
                                                :value="application.id"
                                                :aria-label="`Select application from ${application.user?.name || 'applicant'}`"
                                                data-test="select-application"
                                                class="h-5 w-5 shrink-0 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                            />
                                        </div>

                                            <div class="flex text-sm justify-between p-4">
                                                <span class="text-gray-900 font-bold pr-2">Started On</span>
                                                <span class="inline-flex items-center gap-2 text-gray-500">
                                                    <font-awesome-icon :icon="faCalendar" class="h-4 w-4 text-gray-400" aria-hidden="true" />
                                                    {{ application.started_on_display || application.created_at }}
                                                </span>
                                            </div>

                                            <div class="flex text-sm justify-between items-center p-4">
                                                <span class="text-gray-900 font-bold pr-2">Progress</span>
                                                <span v-if="application.is_complete" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Complete</span>
                                                <span v-else class="text-gray-500 flex items-center gap-2 min-w-[6rem]">
                                                    <div class="relative group flex-1 min-w-0 w-16">
                                                        <div class="bg-gray-200 rounded-full h-2 cursor-pointer">
                                                            <div
                                                                class="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                                                                :style="{ width: (application.completion_percentage ?? 0) + '%' }"
                                                            ></div>
                                                        </div>
                                                        <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-20 pointer-events-none">
                                                            <div class="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                                                {{ application.steps_completed ?? 0 }}/{{ application.total_steps ?? 0 }} ({{ application.current_step_display_name || 'Getting Started' }})
                                                                <div class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span class="text-xs text-gray-600 font-medium shrink-0">{{ application.completion_percentage ?? 0 }}%</span>
                                                </span>
                                            </div>

                                            <div class="flex text-sm justify-between items-center p-4">
                                                <span class="inline-flex items-center text-gray-900 font-bold pr-2">
                                                    Risk
                                                    <ToolTip
                                                        title="Risk"
                                                        :description="riskMeterTooltipDescription"
                                                    />
                                                </span>
                                                <div class="w-[60px] flex justify-center shrink-0">
                                                    <RiskMeter :addon-count="(application.required_addons?.length || 0) + (hasProtectAccess ? 1 : 0)" :width="60" />
                                                </div>
                                            </div>

                                            <div v-if="!application.personal_information" class="flex text-sm justify-between items-center p-4">
                                                <span class="text-gray-900 font-bold">Personal Information</span>
                                                <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium">
                                                    <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                </span>
                                            </div>
                                            <div v-if="!application.personal_information" class="flex text-sm text-red-700 font-bold p-4">
                                                Please ask the applicant to submit personal information.
                                            </div>
                                            
                                            <div class="flex flex-col items-stretch gap-2 p-4">
                                                <button
                                                    v-if="application.required_addons?.length < addons.length"
                                                    type="button"
                                                    :disabled="isSharedViewOnly"
                                                    class="inline-flex items-center justify-center gap-2 rounded-md bg-rent-risk-blue-400 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 disabled:pointer-events-none disabled:opacity-50"
                                                    @click="openEnhancedScreeningOptionsModal(application)"
                                                >
                                                    <font-awesome-icon :icon="faShieldCheck" class="h-3.5 w-3.5" aria-hidden="true" />
                                                    Lower My Risk
                                                </button>
                                                <button
                                                    v-if="application.required_addons?.length"
                                                    type="button"
                                                    :disabled="isSharedViewOnly"
                                                    class="inline-flex items-center justify-center gap-2 rounded-md bg-rent-risk-blue-400 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 disabled:pointer-events-none disabled:opacity-50"
                                                    @click="showApplicationEnhancedScreeningOptions(application)"
                                                >
                                                    Verified Reports
                                                    <font-awesome-icon :icon="faChevronRight" class="h-3 w-3" aria-hidden="true" />
                                                </button>
                                            </div>

                                            <template v-if="property.required_two_step">
                                                <div class="flex text-sm justify-between items-center p-4">
                                                    <span class="text-gray-900 font-bold">Criminal/Background Report</span>
                                                    <template v-if="application.agent_requests_criminal_background && !application.tenant_approves_criminal_background">
                                                        <span class="whitespace-nowrap text-sm text-green-700 font-bold text-center">
                                                            Request Sent
                                                        </span>
                                                    </template>
                                                    <template v-else-if="application.agent_requests_criminal_background && application.tenant_approves_criminal_background">
                                                        <span
                                                            class="whitespace-nowrap text-sm text-gray-500 text-center">
                                                            <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                                <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-if="application.share_id">
                                                                    <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                                </span>
                                                                <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-else>
                                                                    <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                                </span>
                                                            </span>
                                                        </span>
                                                    </template>
                                                    <template v-else>
                                                        <span v-if="application.report"
                                                            class="whitespace-nowrap text-sm text-gray-500 text-center">
                                                            <button
                                                                type="button"
                                                                :disabled="isSharedViewOnly"
                                                                class="inline-flex items-center justify-center px-2 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 uppercase disabled:pointer-events-none disabled:opacity-50"
                                                                @click.prevent="openRequestModalAlert(application)"
                                                            >
                                                                Request
                                                            </button>
                                                        </span>
                                                        <span
                                                            class="whitespace-nowrap text-sm text-gray-500 text-center" v-else>
                                                            N/A
                                                        </span>
                                                    </template>
                                                </div>
                                            </template>
                                            <div v-if="application.personal_information || application.agent_application_display === 0 || !isSharedViewOnly">
                                                <button
                                                    v-if="application.personal_information"
                                                    type="button"
                                                    @click="clickList(application)"
                                                    :class="[application.agent_application_display === 0 ? 'hover:bg-red-100' : 'hover:bg-gray-50', 'flex w-full items-center justify-center gap-x-2 border-t border-gray-100 py-4 text-sm font-semibold text-gray-900']"
                                                >
                                                    View Application
                                                    <font-awesome-icon :icon="faChevronRight" class="h-3 w-3" aria-hidden="true" />
                                                </button>
                                                <button
                                                    v-if="!isSharedViewOnly && application.agent_application_display === 1"
                                                    type="button"
                                                    :disabled="isArchivingApplications"
                                                    data-test="archive-application"
                                                    class="flex w-full items-center justify-center gap-x-2 border-t border-gray-100 py-4 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:text-red-700 disabled:pointer-events-none disabled:opacity-50"
                                                    @click="openArchiveApplicationsModal([application.id])"
                                                >
                                                    <font-awesome-icon :icon="faBoxArchive" class="h-3.5 w-3.5" aria-hidden="true" />
                                                    Archive
                                                </button>
                                                <button
                                                    v-else-if="application.agent_application_display === 0"
                                                    type="button"
                                                    :disabled="isSharedViewOnly"
                                                    @click="restoreApplication(application)"
                                                    class="flex w-full items-center justify-center gap-x-2 border-t border-gray-100 py-4 text-sm font-semibold text-gray-900 hover:bg-red-100 disabled:pointer-events-none disabled:opacity-50"
                                                >
                                                    <font-awesome-icon icon="fa-refresh" /> Restore
                                                </button>
                                            </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Restore Application Modal -->
        <TransitionRoot as="template" :show="showRestoreApplicationModal">
            <Dialog as="div" class="relative z-50" @close="showRestoreApplicationModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                        <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this application?</DialogTitle>
                                        <div class="mt-2">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex">
                                    <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreApplicationModal = false">Cancel</button>
                                    <button type="button" @click="confirmRestoreApplication" class="text-center flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Confirm</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
        

        <!-- Archive Applications Modal (86bc476zp): one row or the whole selection -->
        <TransitionRoot as="template" :show="showArchiveApplicationsModal">
            <Dialog as="div" class="relative z-50" @close="showArchiveApplicationsModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                        <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                                            {{ pendingArchiveIds.length === 1 ? 'Archive this application?' : `Archive ${pendingArchiveIds.length} applications?` }}
                                        </DialogTitle>
                                        <div class="mt-2 text-sm text-gray-600">
                                            Archived applications are hidden from this list. You can show them again with the Show Archived Applications toggle and restore any of them later.
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex">
                                    <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showArchiveApplicationsModal = false">Cancel</button>
                                    <button type="button" :disabled="isArchivingApplications" data-test="confirm-archive-applications" class="text-center flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60" @click="confirmArchiveApplications">Archive</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Enhanced Screening Results Modal -->
        <EnhancedScreeningResultsModal
            :show="showEnhancedScreeningResultsModal"
            :application="application"
            :property="property"
            @close="showEnhancedScreeningResultsModal = false"
        />
        
        <!-- Enhanced Screening Options Modal -->
        <EnhancedScreeningOptionsModal
            ref="enhancedScreeningModalRef"
            :show="showEnhancedScreeningOptionsModal"
            :application="application"
            :addons="addons"
            :has-protect-access="hasProtectAccess"
            :subscription-type="subscriptionType"
            :free-bundles-used="freeBundlesUsed"
            :free-eso-offer-used="freeEsoOfferUsed"
            :payment-method-success="payment_method_success"
            :show-auto-eso-option="showAutoEsoOptionInModal"
            @close="closeEnhancedScreeningOptionsModal"
            @submit="handleEnhancedScreeningSubmit"
        />

        <!-- RAC-10337: Verified Reports upsell shown before the application detail
             page. Non-Pro landlords see it on every click; Pro landlords once per
             applicant. -->
        <VerifiedReportsUpsellModal
            :show="showVerifiedReportsUpsellModal"
            @accept="handleVerifiedReportsUpsellAccept"
            @decline="handleVerifiedReportsUpsellDecline"
        />

        <!-- RAC-10007: RiskGuard Pro upsell shown before the ESO selection
             modal for users on free / RiskGuard ($7.99). Free users have no
             decline path — upgrade is their only option to access ESO. -->
        <RiskGuardProUpsellModal
            :show="showRiskGuardProUpsellModal"
            :processing="isUpgradingToProFromUpsell"
            :stripe-data="upsellStripeData"
            :notice="tierChangedNotice"
            decline-label="I'm fine verifying for $29"
            @dismiss="closeRiskGuardProUpsell"
            @upgrade="handleRiskGuardProUpsellUpgrade"
            @decline="handleRiskGuardProUpsellDecline"
        />

        <!-- Criminal Background Request Alert -->
        <CriminalBackgroundRequestAlert
            v-if="showRequestModal"
            :application="application"
            :open="showRequestModal"
            @close-alert="showRequestModal = false"
        />
        
        <!-- SSN Acknowledgment Modal -->
        <SsnAcknowledgmentModal
            :show="!property.ssn_acknowledgment"
            @closeAlert="handleCloseModal"
        />
        
        <!-- CA ICRA Compliance Component -->
        <CaIccraCompliance
            :open="showCAComplianceModal"
            :property="property"
            @close-alert="handleComplianceClose"
        />

        <!-- Auto-ESO Subscriber Opt-In Confirmation Modal -->
        <TransitionRoot as="template" :show="showAutoEsoConfirmModal">
            <Dialog as="div" class="relative z-50" @close="closeAutoEsoConfirmModal">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>
                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                        <font-awesome-icon :icon="faShieldCheck" class="h-6 w-6 text-blue-600" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Enable Automatic Verified Screening</DialogTitle>
                                        <div class="mt-3 text-left space-y-4">
                                            <p class="text-sm text-gray-600">Set your risk level once. Protect yourself and every applicant automatically.</p>
                                            <fieldset class="space-y-3">
                                                <legend class="sr-only">Who pays for enhanced screening</legend>
                                                <label class="flex gap-3 cursor-pointer items-start rounded-md border border-gray-200 p-3 has-[:checked]:border-rent-risk-blue-400 has-[:checked]:bg-blue-50/50">
                                                    <input v-model="autoEsoPayerChoice" type="radio" :value="AUTO_ESO_PAYER_AGENT" class="mt-1 h-4 w-4 border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-700">I pay for verified screening for each applicant.</span>
                                                </label>
                                                <label v-if="autoEsoPayerChoice === AUTO_ESO_PAYER_AGENT" class="flex gap-3 cursor-pointer items-start pl-1">
                                                    <input v-model="autoEsoAgentAck" type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-600">I understand that I will be billed ${{ protectDiscountedTotal }} each time a new applicant starts an application for this property (RiskGuard Pro subscribers get 2 free monthly reports and will be billed ${{ protectDiscountedTotal }}/report after their 2 free enhanced reports).</span>
                                                </label>
                                                <label class="flex gap-3 cursor-pointer items-start rounded-md border border-gray-200 p-3 has-[:checked]:border-rent-risk-blue-400 has-[:checked]:bg-blue-50/50">
                                                    <input v-model="autoEsoPayerChoice" type="radio" :value="AUTO_ESO_PAYER_APPLICANT" class="mt-1 h-4 w-4 border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-700">Request that each applicant verify their own report and pay ${{ protectDiscountedTotal }}.</span>
                                                </label>
                                                <label v-if="autoEsoPayerChoice === AUTO_ESO_PAYER_APPLICANT" class="flex gap-3 cursor-pointer items-start pl-1">
                                                    <input v-model="autoEsoApplicantAck" type="checkbox" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500" />
                                                    <span class="text-sm text-gray-600">I understand that applicants will be asked to pay ${{ protectDiscountedTotal }} and complete enhanced screening unless they remove it at checkout.</span>
                                                </label>
                                            </fieldset>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex gap-2">
                                    <button type="button" class="flex-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="closeAutoEsoConfirmModal">Cancel</button>
                                    <button type="button" :disabled="autoEsoLoading || !canSubmitAutoEsoEnable" @click="confirmEnableAutoEso" class="flex-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
                                        <span v-if="autoEsoLoading">Enabling...</span>
                                        <span v-else>Enable Auto-Verification</span>
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Account-wide auto-ESO lock explanation (RAC-10476) -->
        <TransitionRoot as="template" :show="showAccountWideAutoEsoInfo">
            <Dialog as="div" class="relative z-50" @close="showAccountWideAutoEsoInfo = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>
                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                    <font-awesome-icon :icon="faShieldCheck" class="h-6 w-6 text-blue-600" aria-hidden="true" />
                                </div>
                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Maximum Risk Reduction is on for all your properties</DialogTitle>
                                    <p class="mt-3 text-sm text-gray-600">To change it for just this property, turn off the account-wide option on the Properties page.</p>
                                </div>
                                <div class="mt-5 sm:mt-6 flex gap-2">
                                    <button type="button" class="flex-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showAccountWideAutoEsoInfo = false">Close</button>
                                    <Link :href="route('properties.index')" class="flex-1 text-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Go to Properties</Link>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

    </div>
</template>

<script setup>
import { appUrl } from '@/appUrl'
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import { Link, router, useForm, usePage } from '@inertiajs/vue3'
import { Switch, SwitchGroup, SwitchLabel, Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faInfoCircle, faCircleCheck, faCircleXmark, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { faArrowsRotate, faShieldCheck, faLink, faFileLines, faUser, faCalendar, faChevronRight, faBoxArchive } from '@fortawesome/pro-light-svg-icons'
import axios from 'axios'
import EnhancedScreeningOptionsModal from '@/Components/EnhancedScreeningOptionsModal.vue'
import RiskGuardProUpsellModal from '@/Components/RiskGuardProUpsellModal.vue'
import VerifiedReportsUpsellModal from '@/Components/VerifiedReportsUpsellModal.vue'
import EnhancedScreeningResultsModal from '@/Components/EnhancedScreeningResultsModal.vue'
import CriminalBackgroundRequestAlert from '@/Shared/CriminalBackgroundRequestAlert.vue'
import SsnAcknowledgmentModal from '@/Shared/SsnAcknowledgmentModal.vue'
import CaIccraCompliance from '@/Shared/CaIccraCompliance.vue'
import RiskMeter from '@/Components/RiskMeter.vue'
import ToolTip from '@/Shared/ToolTip.vue'
import HowItWorksBanner from '@/Pages/V2/Properties/Components/HowItWorksBanner.vue'
import { shouldShowVerifiedReportsUpsell as shouldShowUpsell } from './verifiedReportsUpsell'
import { autoEsoToggleClickAction } from './autoEsoToggleClick'

// Route function is available globally via Ziggy
const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const protectDiscountedTotal = import.meta.env.VITE_ADDONS_DISCOUNTED_PRICE_TOTAL ?? '29'

const AUTO_ESO_PAYER_AGENT = 1
const AUTO_ESO_PAYER_APPLICANT = 2

const props = defineProps({
    property: {
        type: Object,
        required: true
    },
    tab: {
        type: String,
        default: 'tenant-screening'
    },
    tabData: {
        type: Object,
        default: () => ({})
    },
    addons: {
        type: Array,
        default: () => []
    },
    hasProtectAccess: {
        type: Boolean,
        default: false
    },
    subscriptionType: {
        type: String,
        default: null
    },
    freeBundlesUsed: {
        type: Number,
        default: 0
    },
    freeEsoOfferUsed: {
        type: Boolean,
        default: false
    },
    isUserExperianVerified: {
        type: Boolean,
        default: false
    },
    isNewAgent: {
        type: Boolean,
        default: false
    },
    protectTosAcceptedAt: {
        type: Boolean,
        default: false
    },
    isAgentPaymentMethodAdded: {
        type: Boolean,
        default: false
    },
    userPaymentMethod: {
        type: Object,
        default: null
    },
    stripeData: {
        type: [Object, Array],
        default: () => ({})
    },
    subscriptionProtectionRef: {
        type: Object,
        default: null
    },
    userGlobalAutoEso: {
        type: Object,
        default: () => ({
            is_active: false,
            payer: null,
            effective_billing: false,
            payment_method_required_but_missing: false
        })
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
    assignedBy: {
        type: Object,
        default: null,
    },
    viewOnlyTooltip: {
        type: Object,
        default: null,
    },
})

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')
const howItWorksSteps = [
    {
        icon: faLink,
        title: 'Share your link',
        description: 'Share your application link with prospective tenants.',
    },
    {
        icon: faFileLines,
        title: 'View applications',
        description: 'See an applicant\'s credit, criminal and eviction history.',
    },
    {
        icon: faShieldCheck,
        title: 'Request verified reports',
        description: 'Get verified income, employment, identity and assets.',
    },
    {
        icon: faUser,
        title: 'Make a decision',
        description: 'Compare applicants and choose the best fit with confidence.',
    },
]

const applicantInitials = (name) => {
    if (!name) {
        return '?'
    }

    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
}

const hasCoApplicantInvited = (application) => Boolean(application.additional_applicants_share_hash)

const hasApplicantProfilePhoto = (application) => Boolean(application.user?.profile_photo_path)

const applicationStatusBarClass = (application) => {
    if (application.agent_application_display === 0) {
        return 'bg-red-300'
    }

    if (application.meets_screening_rules === false) {
        return 'bg-amber-400'
    }

    if (application.is_complete || (application.completion_percentage ?? 0) >= 70) {
        return 'bg-emerald-400'
    }

    return 'bg-amber-300'
}

const showArchived = ref(false)

const userGlobalAutoEsoActive = computed(() => props.userGlobalAutoEso?.is_active === true)

const userGlobalAutoEsoTooltipDescription = computed(() => {
    if (!userGlobalAutoEsoActive.value) {
        return ''
    }
    const href = route('properties.index')
    const link = `<a href="${href}" class="font-semibold text-rent-risk-blue-600 underline hover:text-rent-risk-blue-500">Properties</a>`
    // RAC-10476: under the applicant-paid default a property with its own setting
    // keeps it, so "follows your Properties list setting" would be untrue here.
    if (props.userGlobalAutoEso?.is_default && props.property.auto_eso_payer != null) {
        const payerLabel = props.property.auto_eso_payer === AUTO_ESO_PAYER_AGENT ? 'landlord-paid' : 'applicant-paid'
        return `Account-wide automatic enhanced screening is on. This property keeps its own ${payerLabel} setting. To change screening per property, turn the account-wide option off on the ${link} page first.`
    }
    return `Account-wide automatic enhanced screening is on. This property follows your ${link} list setting. To change screening per property, turn that off on the Properties page first.`
})

// State for auto-ESO toggle (on when user-level is active, even if this property is off in DB)
const autoEsoEnabled = ref(
    userGlobalAutoEsoActive.value ? true : (props.property.auto_eso_enabled ?? false)
)
const showAutoEsoOptionInModal = computed(() => !autoEsoEnabled.value && !userGlobalAutoEsoActive.value)
const showAutoEsoConfirmModal = ref(false)
// RAC-10413: set when the landlord picked agent-paid auto-ESO but had no card on
// file; the userPaymentMethod watch finishes the enable once the card lands. Every
// OTHER flow that can produce a card (report payment, Pro subscribe) must disarm
// this on entry — otherwise a card added for something else would fire an enable
// the landlord already backed out of at the add-card popup.
const pendingAutoEsoEnableAfterCard = ref(false)
const autoEsoLoading = ref(false)
const autoEsoPayerChoice = ref(null)
const autoEsoAgentAck = ref(false)
const autoEsoApplicantAck = ref(false)

const canSubmitAutoEsoEnable = computed(() => {
    if (autoEsoPayerChoice.value === AUTO_ESO_PAYER_AGENT) {
        return autoEsoAgentAck.value
    }
    if (autoEsoPayerChoice.value === AUTO_ESO_PAYER_APPLICANT) {
        return autoEsoApplicantAck.value
    }
    return false
})

watch(
    () => [props.property.auto_eso_enabled, props.userGlobalAutoEso],
    () => {
        if (props.userGlobalAutoEso?.is_active) {
            autoEsoEnabled.value = true
        } else {
            autoEsoEnabled.value = props.property.auto_eso_enabled ?? false
        }
    },
    { deep: true }
)

// State for application interactions
const showErrorMessage = ref({})
const application = ref(null)
const pendingApplication = ref(null)
const isInSubscriptionFlow = ref(false)

// State for modals
const showRestoreApplicationModal = ref(false)
const showRequestModal = ref(false)
const showCAComplianceModal = ref(false)

// State for enhanced screening modals
const showEnhancedScreeningOptionsModal = ref(false)
// RAC-10007: upsell to RiskGuard Pro before opening the ESO selection modal
// for users on the free plan or RiskGuard $7.99 plan.
const showRiskGuardProUpsellModal = ref(false)
// Why the pop-up reopened (the Pro price moved before the charge); shown inside it.
const tierChangedNotice = ref(null)
const closeRiskGuardProUpsell = () => {
    showRiskGuardProUpsellModal.value = false
    tierChangedNotice.value = null
}
const page = usePage()
// The upsell's embedded "Switch to new plan" can collect a card in place when a SetupIntent is here.
const upsellStripeData = computed(() => (props.stripeData?.publishable_key && props.stripeData?.setup_intent ? props.stripeData : null))
const isUpgradingToProFromUpsell = ref(false)
// RAC-10337: Verified Reports upsell shown on "View Application". Pro landlords see
// it once per applicant — the server records that in applications.eso_upsell_seen_at,
// and this set covers the same-page repeat click before any reload.
const showVerifiedReportsUpsellModal = ref(false)
const verifiedReportsUpsellApplication = ref(null)
const esoUpsellSeenApplicationIds = ref(new Set())
const showEnhancedScreeningResultsModal = ref(false)
const enhancedScreeningModalRef = ref(null)
const payment_method_success = ref(null)
const isSubscribingToProtect = ref(false)
const payment_method = ref(props.userPaymentMethod)
const pendingEnhancedScreeningData = ref(null)
const pendingEsoUpsellSubscription = ref(false)

// Emit events to parent
const emit = defineEmits(['request-add-card', 'request-share-modal'])

// Note: Restore uses GET requests, not forms

// Computed property for applications
const applications = computed(() => {
    return props.tabData?.applications || []
})

const screeningRulesFlagTooltip = 'Applicant does not meet your current rule set.'
const riskMeterTooltipDescription = 'Every rental application carries some level of risk, especially in today\'s market where 84% of U.S. landlords experience application fraud. The Risk Meter visualizes your estimated risk level and updates as additional verification is completed. By requesting Verified Reports, such as identity, income, employment, and asset verification, you can reduce fraud risk, eliminate uncertainty, and make more confident leasing decisions.'

watch(() => props.userPaymentMethod, (newVal, oldVal) => {
    if (newVal) {
        payment_method.value = newVal

        // If payment method was just added and we have pending enhanced screening data, process it
        if (!oldVal && newVal && pendingEnhancedScreeningData.value) {
            processEnhancedScreeningPayment(pendingEnhancedScreeningData.value)
        }

        // RAC-10413: card just landed for a pending agent-paid auto-ESO enable
        if (!oldVal && newVal && pendingAutoEsoEnableAfterCard.value) {
            pendingAutoEsoEnableAfterCard.value = false
            autoEsoLoading.value = true
            router.put(route('properties.toggle-auto-eso', props.property.id), {
                enabled: true,
                payer: AUTO_ESO_PAYER_AGENT,
            }, {
                preserveScroll: true,
                onSuccess: () => {
                    autoEsoEnabled.value = true
                },
                onFinish: () => {
                    autoEsoLoading.value = false
                },
            })
        }
    }
}, { immediate: true })

// Function to check and show CA compliance modal if needed
const checkAndShowCACompliance = () => {
    // Only show CA compliance modal if:
    // 1. Property is in California
    // 2. CA compliance is not yet acknowledged
    // 3. SSN acknowledgment is already done (so it doesn't conflict with SSN modal)
    // 4. Modal is not already showing
    if (props.property.state === 'CA' && 
        !props.property.ca_icraa_compliance && 
        props.property.ssn_acknowledgment && 
        !showCAComplianceModal.value) {
        showCAComplianceModal.value = true
    }
}

// Watch for property changes to show CA compliance modal if needed
watch(() => [props.property.state, props.property.ca_icraa_compliance, props.property.ssn_acknowledgment], () => {
    checkAndShowCACompliance()
}, { immediate: true })

// Also check on mount in case property data is already loaded
onMounted(() => {
    checkAndShowCACompliance()
})

// Method for show archived toggle
const show_archived_applications = () => {
    showArchived.value = !showArchived.value
}

const openScreenApplicantsModal = () => {
    if (isSharedViewOnly.value) {
        return
    }
    emit('request-share-modal')
}

// Auto-ESO toggle handler
const handleAutoEsoToggleClick = () => {
    const action = autoEsoToggleClickAction({
        isSharedViewOnly: isSharedViewOnly.value,
        isAccountWideActive: userGlobalAutoEsoActive.value,
        isEnabled: autoEsoEnabled.value,
    })

    if (action === 'explain') {
        showAccountWideAutoEsoInfo.value = true
    } else if (action === 'disable') {
        disableAutoEso()
    } else if (action === 'enable') {
        openAutoEsoConfirmModal()
    }
}

// RAC-10476: account-wide auto-ESO (now on by default) locks the per-property
// toggle, so a click explains where to change it instead of doing nothing.
const showAccountWideAutoEsoInfo = ref(false)

const closeAutoEsoConfirmModal = () => {
    showAutoEsoConfirmModal.value = false
    autoEsoPayerChoice.value = null
    autoEsoAgentAck.value = false
    autoEsoApplicantAck.value = false
}

// RAC-10413: fresh modal open = fresh intent; drop any abandoned pending enable.
// (The other card-producing flows also disarm the flag on entry — see its declaration.)
const openAutoEsoConfirmModal = () => {
    pendingAutoEsoEnableAfterCard.value = false
    showAutoEsoConfirmModal.value = true
}

const confirmEnableAutoEso = () => {
    if (!canSubmitAutoEsoEnable.value || autoEsoPayerChoice.value === null) {
        return
    }

    // RAC-10413: landlord-paid auto-ESO needs a card on file (not a subscription) —
    // the per-applicant fee is charged to that card. No card yet → collect one via the
    // shared add-card modal, then enable once it lands (see the userPaymentMethod watch).
    if (autoEsoPayerChoice.value === AUTO_ESO_PAYER_AGENT && payment_method.value === null) {
        pendingAutoEsoEnableAfterCard.value = true
        closeAutoEsoConfirmModal()
        emit('request-add-card', {
            heading: 'Enable Automatic Verified Screening',
            noticeText: `A card on file is required for landlord-paid screening. It will be charged $${protectDiscountedTotal} each time a new applicant starts an application.`,
            confirmButtonText: 'Add Card & Enable',
        })
        return
    }

    autoEsoLoading.value = true
    router.put(route('properties.toggle-auto-eso', props.property.id), {
        enabled: true,
        payer: autoEsoPayerChoice.value,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            autoEsoEnabled.value = true
            closeAutoEsoConfirmModal()
        },
        onFinish: () => {
            autoEsoLoading.value = false
        },
    })
}

const disableAutoEso = () => {
    autoEsoLoading.value = true
    router.put(route('properties.toggle-auto-eso', props.property.id), { enabled: false }, {
        preserveScroll: true,
        onSuccess: () => {
            autoEsoEnabled.value = false
        },
        onFinish: () => {
            autoEsoLoading.value = false
        }
    })
}

// Application click handler
const clickList = (app) => {
    if (!app.personal_information) {
        showApplicationError(app.id, 2500)
        return
    }

    // RAC-10444: non-subscribing "new agents" were capped at one application per property.
    // A check-access round-trip gated this click and, once the cap was spent, raised a
    // RiskGuard Pro upsell with no way through — the landlord could not reach the report at
    // all. Views are now unlimited, so the check and that modal are both gone; every
    // applicant gets the same dismissible Verified Reports upsell (RAC-10337) below.
    if (shouldShowVerifiedReportsUpsell(app)) {
        openVerifiedReportsUpsell(app)
        return
    }

    navigateToApplication(app)
}

// RAC-10337: gate the application detail page behind the Verified Reports upsell.
const shouldShowVerifiedReportsUpsell = (app) => shouldShowUpsell({
    application: app,
    addons: props.addons,
    subscriptionType: props.subscriptionType,
    isSharedViewOnly: isSharedViewOnly.value,
    seenApplicationIds: esoUpsellSeenApplicationIds.value,
})

const openVerifiedReportsUpsell = (app) => {
    verifiedReportsUpsellApplication.value = app
    showVerifiedReportsUpsellModal.value = true

    // Non-Pro landlords see the upsell every time, so only record the first
    // impression — the timestamp is what caps Pro landlords at one per applicant.
    if (app.eso_upsell_seen_at || esoUpsellSeenApplicationIds.value.has(app.id)) {
        return
    }

    esoUpsellSeenApplicationIds.value.add(app.id)
    axios.post(route('properties.application.eso-upsell-seen', [app.property_id, app.id]))
        .catch(() => {
            // Best-effort bookkeeping: failing to record the impression must never
            // block the landlord from reaching the application.
        })
}

const handleVerifiedReportsUpsellAccept = () => {
    const app = verifiedReportsUpsellApplication.value
    showVerifiedReportsUpsellModal.value = false
    verifiedReportsUpsellApplication.value = null

    if (!app) {
        return
    }

    // Straight into the existing ESO selection modal — the upsell above already
    // did the convincing, so we skip the RAC-10007 Pro upsell on this path.
    application.value = app
    pendingApplication.value = null
    showEnhancedScreeningOptionsModal.value = true
}

const handleVerifiedReportsUpsellDecline = () => {
    const app = verifiedReportsUpsellApplication.value
    showVerifiedReportsUpsellModal.value = false
    verifiedReportsUpsellApplication.value = null

    if (app) {
        navigateToApplication(app)
    }
}

// Helper methods
const showApplicationError = (applicationId, duration = 3000) => {
    showErrorMessage.value[applicationId] = true
    setTimeout(() => {
        showErrorMessage.value[applicationId] = false
    }, duration)
}

const navigateToApplication = (application) => {
    router.get(appUrl(`/properties/${application.property_id}/application/${application.id}`))
}

// Archive (86bc476zp): per-row or the checked selection, through one endpoint.
const selectedApplicationIds = ref([])
const pendingArchiveIds = ref([])
const showArchiveApplicationsModal = ref(false)
const isArchivingApplications = ref(false)

const archivableApplications = computed(() =>
    applications.value.filter((app) => app.agent_application_display === 1)
)

const allArchivableSelected = computed(() =>
    archivableApplications.value.length > 0
    && archivableApplications.value.every((app) => selectedApplicationIds.value.includes(app.id))
)

const toggleSelectAllApplications = () => {
    selectedApplicationIds.value = allArchivableSelected.value
        ? []
        : archivableApplications.value.map((app) => app.id)
}

const openArchiveApplicationsModal = (ids) => {
    if (isSharedViewOnly.value || ids.length === 0) {
        return
    }
    pendingArchiveIds.value = [...ids]
    showArchiveApplicationsModal.value = true
}

const confirmArchiveApplications = () => {
    isArchivingApplications.value = true

    router.post(route('properties.applications.archive', props.property.id), {
        application_ids: pendingArchiveIds.value,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            selectedApplicationIds.value = selectedApplicationIds.value.filter((id) => !pendingArchiveIds.value.includes(id))
            pendingArchiveIds.value = []
        },
        onFinish: () => {
            isArchivingApplications.value = false
            showArchiveApplicationsModal.value = false
        },
    })
}

// Restore methods
const restoreApplication = (app) => {
    if (isSharedViewOnly.value) {
        return
    }
    application.value = app
    showRestoreApplicationModal.value = true
}

const confirmRestoreApplication = () => {
    router.get(route('properties.application.restore', [props.property.id, application.value.id]), {}, {
        onFinish: () => {
            showRestoreApplicationModal.value = false
            application.value = null
        }
    })
}

// Enhanced screening methods
const showApplicationEnhancedScreeningOptions = (app) => {
    if (isSharedViewOnly.value) {
        return
    }
    showEnhancedScreeningResultsModal.value = true
    application.value = app
}

const openEnhancedScreeningOptionsModal = (app) => {
    if (isSharedViewOnly.value) {
        return
    }
    application.value = app
    pendingApplication.value = null

    // RAC-10007: agents on free / RiskGuard ($7.99) get the Pro upsell first.
    // Pro subscribers go straight to the ESO selection modal.
    if (props.subscriptionType !== 'rentrisk-protect-pro') {
        showRiskGuardProUpsellModal.value = true
        return
    }

    showEnhancedScreeningOptionsModal.value = true
}

const closeEnhancedScreeningOptionsModal = () => {
    enhancedScreeningForm.addons = []
    showEnhancedScreeningOptionsModal.value = false
}

// RAC-10007: user accepted the upsell -> kick off the Pro subscription flow,
// reusing the SubscriptionProtection plumbing already used by the inline
// "subscribe to Pro" checkbox elsewhere in this tab.
const openEnhancedScreeningModalWithRecommendedSelection = async () => {
    showEnhancedScreeningOptionsModal.value = true
    await nextTick()
    enhancedScreeningModalRef.value?.selectAllAddons()
}

const handleRiskGuardProUpsellUpgrade = () => {
    tierChangedNotice.value = null
    pendingAutoEsoEnableAfterCard.value = false
    isUpgradingToProFromUpsell.value = true
    pendingEsoUpsellSubscription.value = true
    // 86bcd9hg3: Pro is one yearly plan for the account, charged at the quoted tier.
    sessionStorage.setItem('pending_subscription_plan', JSON.stringify({
        planSlug: 'protect-pro-tiered',
        billingCycle: 'annual',
        expectedTierSlug: page.props.auth?.user?.pro_plan?.quote_tier?.slug ?? null,
    }))
    isSubscribingToProtect.value = true
    isInSubscriptionFlow.value = true
    showRiskGuardProUpsellModal.value = false
    if (props.subscriptionProtectionRef) {
        props.subscriptionProtectionRef.initiateSubscription(application.value, false)
    }
    isUpgradingToProFromUpsell.value = false
}

// RAC-10337: "I'm fine verifying for $29" -> bypass the Pro upsell and drop the
// landlord straight into the ESO selection modal. Rich asked for this bypass to be
// available to everyone (previously it was hidden for free-plan users, who had no
// path forward but to upgrade).
const handleRiskGuardProUpsellDecline = () => {
    closeRiskGuardProUpsell()
    showEnhancedScreeningOptionsModal.value = true
}

// Enhanced screening form
const enhancedScreeningForm = useForm({
    addons: [],
})

const maybePromptAutoEsoAfterReportRequest = (requestAutoEso) => {
    if (!requestAutoEso || autoEsoEnabled.value || userGlobalAutoEsoActive.value) {
        return
    }

    openAutoEsoConfirmModal()
}

const handleEnhancedScreeningSubmit = (data) => {
    pendingAutoEsoEnableAfterCard.value = false
    pendingEnhancedScreeningData.value = data

    if (payment_method.value === null) {
        if (enhancedScreeningModalRef.value) {
            enhancedScreeningModalRef.value.resetSubmissionState()
        }
        showEnhancedScreeningOptionsModal.value = false
        // Request parent to open the shared add card modal with enhanced-screening
        // context so the modal shows what's being purchased instead of the generic
        // "card on file for subscriptions" copy. The card is saved here; the
        // $29/applicant charge runs via processEnhancedScreeningPayment once the
        // card lands (see the userPaymentMethod watch).
        emit('request-add-card', {
            heading: 'Verify This Applicant',
            noticeText: `One-time $${protectDiscountedTotal} charge to run enhanced screening (income, employment & identity verification) on this applicant. RiskGuard Pro members get 2 free reports each month.`,
            confirmButtonText: `Pay $${protectDiscountedTotal}`,
        })
        return false
    }

    processEnhancedScreeningPayment(data)
}

const processEnhancedScreeningPayment = (data) => {
    enhancedScreeningForm.addons = data.addons
    enhancedScreeningForm.put(route('properties.application.required_addons', [props.property.id, application.value.id]), {
        onSuccess: () => {
            maybePromptAutoEsoAfterReportRequest(data?.requestAutoEso)
        },
        onFinish: () => {
            showEnhancedScreeningOptionsModal.value = false
            enhancedScreeningForm.addons = []
            pendingEnhancedScreeningData.value = null
            if (enhancedScreeningModalRef.value) {
                enhancedScreeningModalRef.value.resetSubmissionState()
            }
        },
    })
}

const handleSubscriptionSuccess = async (data) => {
    isInSubscriptionFlow.value = false
    isSubscribingToProtect.value = false

    if (pendingEsoUpsellSubscription.value) {
        pendingEsoUpsellSubscription.value = false
        await openEnhancedScreeningModalWithRecommendedSelection()
        pendingApplication.value = null
        return
    }

    if (enhancedScreeningModalRef.value) {
        enhancedScreeningModalRef.value.selectAllAddons()
    }

    const targetApplication = data?.pendingApplication || pendingApplication.value
    if (targetApplication) {
        navigateToApplication(targetApplication)
    }
    pendingApplication.value = null
}

const handleSubscriptionError = (data) => {
    alert(data?.error || 'An error occurred. Please try again.')
    isInSubscriptionFlow.value = false
    isSubscribingToProtect.value = false
    pendingEsoUpsellSubscription.value = false
}

const handleSubscriptionCancel = () => {
    isInSubscriptionFlow.value = false
    isSubscribingToProtect.value = false
    pendingEsoUpsellSubscription.value = false
}

// Criminal Background Request methods
const openRequestModalAlert = (app) => {
    if (isSharedViewOnly.value) {
        return
    }
    application.value = app
    showRequestModal.value = true
}

// SSN Acknowledgment and CA Compliance methods
const handleCloseModal = () => {
    // Check CA compliance before reload (using current property state)
    const needsCACompliance = !props.property.ca_icraa_compliance && props.property.state === 'CA'
    
    axios.post(route('ssn-acknowledgment'), {
        type: 'property',
        value: props.property.id,
    }).then(response => {
        if (response.data.status) {
            // Reload the page data to get updated property with ssn_acknowledgment = true
            // Reload without 'only' to ensure property prop updates correctly
            router.reload({
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    // Show CA compliance modal if needed (after property data is updated)
                    if (needsCACompliance) {
                        showCAComplianceModal.value = true
                    }
                }
            })
        }
    }).catch(error => {
        console.error('Failed to acknowledge SSN:', error)
    })
}

const handleComplianceClose = () => {
    showCAComplianceModal.value = false
}

// 86bcd9hg3: the Pro price moved before the charge (nothing was charged). Drop the
// pending follow-up and show this tab's pop-up again, with the "$29 instead" option.
const handleTierChanged = (message = null) => {
    handleSubscriptionCancel()
    tierChangedNotice.value = message
    showRiskGuardProUpsellModal.value = true
}

defineExpose({
    handleSubscriptionSuccess,
    handleSubscriptionError,
    handleSubscriptionCancel,
    handleTierChanged,
})
</script>

